// frontend/src/components/citas/AvailabilityScheduler.jsx - CORRECCIÃ“N PARA REPROGRAMACIÃ“N
import React, { useState, useEffect, useCallback, useMemo  } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  Stack,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { format, addDays, startOfWeek, addWeeks, subWeeks, isSameDay, setHours, setMinutes, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import especialistaService from '../../services/especialistaService';

// Configuración de horarios (8:00 AM to 7:00 PM)
const HOURS = [8, 9, 10, 11, 12, 14, 15, 16, 17, 18, 19]; // Excluye 13:00 (1:00 PM)
const DAYS = ['Lun', 'Mar', 'MiÃ©', 'Jue', 'Vie', 'SÃ¡b'];

const AvailabilityScheduler = ({ 
  selectedEspecialista,
  selectedSlots = [], // Slots seleccionados desde el padre
  onSlotsChange, // Función para notificar cambios en selección
  onCitaClick,
  disabled = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Estados
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [selectedCita, setSelectedCita] = useState(null);
  const [citaDialogOpen, setCitaDialogOpen] = useState(false);

  // Calcular fechas de la semana
  const weekDays = useMemo(() => {
    return DAYS.map((_, index) => addDays(currentWeek, index));
    }, [currentWeek]);

  // Cargar disponibilidad del especialista
  const loadAvailability = useCallback(async () => {
    if (!selectedEspecialista?.id) return;
    
    setLoading(true);
    try {
      const fechaInicio = format(currentWeek, 'yyyy-MM-dd');
      const fechaFin = format(addDays(currentWeek, 6), 'yyyy-MM-dd');
      
      const response = await especialistaService.getDisponibilidad(
        selectedEspecialista.id, 
        fechaInicio, 
        fechaFin
      );
      
      setCitas(response.data?.citas || []);
    } catch (error) {
      console.error('âŒ Error cargando disponibilidad:', error);
      setCitas([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEspecialista?.id, currentWeek]);

  // Efecto para cargar datos cuando cambia el especialista o la semana
  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  // âœ… FUNCIÃ“N CORREGIDA - Obtener citas ACTIVAS para una fecha y hora específica
  const getCitaForSlot = useCallback((date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return citas.find(cita => {
      try {
        const citaFecha = cita.fecha.includes('T') ? cita.fecha.split('T')[0] : cita.fecha;
        const citaHora = parseInt(cita.hora_inicio.split(':')[0]);
        
        // âœ… FILTRAR POR ESTADOS - Solo considerar citas que realmente ocupan el horario
        const esActiva = !['Reprogramo', 'Cancelada'].includes(cita.estado);
        
        return citaFecha === dateStr && citaHora === hour && esActiva;
      } catch (error) {
        console.error('Error comparando cita:', error);
        return false;
      }
    });
  }, [citas]);

  // âœ… NUEVA FUNCIÃ“N - Obtener TODAS las citas para un slot (incluyendo reprogramadas para mostrar info histórica)
  const getAllCitasForSlot = useCallback((date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return citas.filter(cita => {
      try {
        const citaFecha = cita.fecha.includes('T') ? cita.fecha.split('T')[0] : cita.fecha;
        const citaHora = parseInt(cita.hora_inicio.split(':')[0]);
        return citaFecha === dateStr && citaHora === hour;
      } catch (error) {
        return false;
      }
    });
  }, [citas]);

  const esCitaGrupal = useCallback((cita) => {
    return cita?.grupo_id !== null && cita?.grupo_id !== undefined;
  }, []);

  // âœ… FUNCIÃ“N CORREGIDA - Obtener citas grupales ACTIVAS en el slot
  const getCitasGrupalesEnSlot = useCallback((date, hour) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return citas.filter(cita => {
      try {
        const citaFecha = cita.fecha.includes('T') ? cita.fecha.split('T')[0] : cita.fecha;
        const citaHora = parseInt(cita.hora_inicio.split(':')[0]);
        
        // âœ… FILTRAR POR ESTADOS ACTIVOS
        const esActiva = !['Reprogramo', 'Cancelada'].includes(cita.estado);
        
        return citaFecha === dateStr && citaHora === hour && esActiva;
      } catch (error) {
        return false;
      }
    });
  }, [citas]);

  // âœ… Verificar si un slot estÃ¡ disponible (ahora ignora citas reprogramadas)
  const isSlotAvailable = useCallback((date, hour) => {
    return !getCitaForSlot(date, hour);
  }, [getCitaForSlot]);

  // Generar clave Ãºnica para slot
  const getSlotKey = (date, hour) => {
    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      const slotKey = `${formattedDate}-${hour}`;
      return slotKey;
    } catch (error) {
      console.error('âŒ ERROR en getSlotKey:', error);
      console.error('âŒ Date problemÃ¡tico:', date);
      return `invalid-${hour}`;
    }
  };

  // Verificar si un slot estÃ¡ seleccionado
  const isSlotSelected = (date, hour) => {
    const slotKey = getSlotKey(date, hour);
    return selectedSlots.includes(slotKey);
  };

  // Manejar click en slot
  const handleSlotClick = (date, hour) => {
    if (disabled) {
      return;
    }
    
    // âœ… Obtener cita ACTIVA (no reprogramadas)
    const citaActiva = getCitaForSlot(date, hour);
    
    if (citaActiva) {
      // âœ… NUEVA FUNCIONALIDAD: Si hay citas históricas tambiÃ©n, mostrar todas
      const todasLasCitas = getAllCitasForSlot(date, hour);
      
      if (todasLasCitas.length > 1) {
        // Mostrar información de todas las citas (activa + históricas)
        const citaInfo = {
          ...citaActiva,
          citasHistoricas: todasLasCitas.filter(c => ['Reprogramo', 'Cancelada'].includes(c.estado))
        };
        setSelectedCita(citaInfo);
      } else {
        setSelectedCita(citaActiva);
      }
      
      setCitaDialogOpen(true);
      return;
    }

    // Slot disponible - alternar selección
    const slotKey = getSlotKey(date, hour);
    const isCurrentlySelected = selectedSlots.includes(slotKey);
    
    let newSelectedSlots;
    if (isCurrentlySelected) {
      // Deseleccionar
      newSelectedSlots = selectedSlots.filter(slot => slot !== slotKey);
    } else {
      // Seleccionar
      newSelectedSlots = [...selectedSlots, slotKey];
    }
    
    // Notificar cambio al componente padre
    if (onSlotsChange) {
      onSlotsChange(newSelectedSlots);
    }
  };

  // Obtener color del estado
  const getEstadoColor = (estado) => {
    const colors = {
      'Programada': '#FF9800',
      'Confirmada': '#4CAF50',
      'Cancelada': '#f44336',
      'Realizada': '#9E9E9E',
      'No asistió': '#FF5722',
      'Reprogramo': '#ff6f00' // Color específico para reprogramadas
    };
    return colors[estado] || '#9E9E9E';
  };

  // âœ… FUNCIÃ“N CORREGIDA - Renderizar celda de cita existente
  const renderCitaCell = (citaActiva, todasLasCitas = null) => {
    const isPersonal = citaActiva.es_evento_personal;
    const isGrupal = esCitaGrupal(citaActiva);
    
    // Verificar si hay citas históricas en el mismo horario
    const citasHistoricas = todasLasCitas ? 
      todasLasCitas.filter(c => ['Reprogramo', 'Cancelada'].includes(c.estado)) : 
      getAllCitasForSlot(new Date(citaActiva.fecha), parseInt(citaActiva.hora_inicio.split(':')[0]))
        .filter(c => ['Reprogramo', 'Cancelada'].includes(c.estado));
    
    const tieneHistorico = citasHistoricas.length > 0;
    
    // Para citas grupales, obtener todas las citas del grupo en este slot
    const citasEnSlot = isGrupal ? getCitasGrupalesEnSlot(
      new Date(citaActiva.fecha), 
      parseInt(citaActiva.hora_inicio.split(':')[0])
    ) : [citaActiva];
    
    const totalPacientes = citasEnSlot.length;
    
    let color;
    if (isPersonal) {
      color = '#6D6D6D';
    } else if (isGrupal) {
      color = '#2196F3'; // Azul para citas grupales
    } else {
      color = getEstadoColor(citaActiva.estado);
    }
    
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: color,
          color: 'white',
          p: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          border: isGrupal ? '2px solid #1976D2' : 
                  tieneHistorico ? '2px dashed #ff6f00' : 'none',
          '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'scale(1.02)',
            zIndex: 2
          },
          transition: 'all 0.2s ease'
        }}
        onClick={(e) => {
          e.stopPropagation();
          
          // âœ… Preparar información completa para el diÃ¡logo
          const citaCompleta = {
            ...citaActiva,
            citasHistoricas: tieneHistorico ? citasHistoricas : []
          };
          
          setSelectedCita(citaCompleta);
          setCitaDialogOpen(true);
        }}
      >
        {/* âœ… NUEVO - Indicador de citas históricas */}
        {tieneHistorico && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.6rem', 
              fontWeight: 'bold',
              bgcolor: 'rgba(255, 111, 0, 0.8)',
              borderRadius: '2px',
              px: 0.5,
              mb: 0.5,
              textAlign: 'center'
            }}
          >
            REPROG ({citasHistoricas.length})
          </Typography>
        )}
        
        {/* Indicador de cita grupal */}
        {isGrupal && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.6rem', 
              fontWeight: 'bold',
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: '4px',
              px: 0.5,
              mb: 0.5,
              textAlign: 'center'
            }}
          >
            GRUPAL ({totalPacientes})
          </Typography>
        )}
        
        <Typography variant="caption" fontWeight="bold" noWrap>
          {isPersonal ? (citaActiva.motivo || 'Evento Personal') :
           isGrupal ? `${totalPacientes} Pacientes` :
           (citaActiva.paciente ? `${citaActiva.paciente.nombres} ${citaActiva.paciente.apellidos}` : 'Sin paciente')}
        </Typography>
        
        {!isPersonal && citaActiva.especialidad && (
          <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
            {citaActiva.especialidad.nombre}
          </Typography>
        )}
        
        {/* Mostrar grupo info si existe */}
        {isGrupal && citaActiva.grupo?.nombre && (
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.6rem' }} noWrap>
            {citaActiva.grupo.nombre}
          </Typography>
        )}
      </Box>
    );
  };

  // Renderizar slot disponible (sin cambios)
  const renderAvailableSlot = (date, hour) => {
    const isSelected = isSlotSelected(date, hour);
    const slotKey = getSlotKey(date, hour);
    const isHovered = hoveredSlot === slotKey;
    
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          border: isSelected ? `2px solid ${theme.palette.primary.main}` : `1px dashed ${theme.palette.divider}`,
          borderRadius: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isSelected 
            ? alpha(theme.palette.primary.main, 0.15)
            : isHovered 
              ? alpha(theme.palette.primary.main, 0.05)
              : 'transparent',
          '&:hover': disabled ? {} : {
            backgroundColor: isSelected 
              ? alpha(theme.palette.primary.main, 0.2)
              : alpha(theme.palette.primary.main, 0.1),
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
            borderStyle: 'solid'
          },
          transition: 'all 0.2s ease',
          opacity: disabled ? 0.5 : 1,
          position: 'relative',
          zIndex: 1
        }}
        onMouseEnter={() => !disabled && setHoveredSlot(slotKey)}
        onMouseLeave={() => setHoveredSlot(null)}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSlotClick(date, hour);
        }}
      >
        {isSelected ? (
          <CheckIcon 
            sx={{ 
              color: theme.palette.primary.main, 
              fontSize: isMobile ? 16 : 20 
            }} 
          />
        ) : (isHovered && !disabled) ? (
          <AddIcon 
            sx={{ 
              color: theme.palette.primary.main, 
              fontSize: isMobile ? 16 : 20 
            }} 
          />
        ) : (
          <Typography 
            variant="caption" 
            color="text.secondary"
            sx={{ opacity: 0.5 }}
          >
            +
          </Typography>
        )}
      </Box>
    );
  };

  // Limpiar todas las selecciones
  const handleClearAll = () => {
    if (onSlotsChange) {
      onSlotsChange([]);
    }
  };

  // Convertir slot key a información legible (sin cambios - función ya funcionaba)
  const formatSlotInfo = (slotKey) => {
    try {
      if (!slotKey || typeof slotKey !== 'string') {
        throw new Error(`SlotKey invÃ¡lido: ${slotKey} (tipo: ${typeof slotKey})`);
      }
      
      const lastDashIndex = slotKey.lastIndexOf('-');
      
      if (lastDashIndex === -1) {
        throw new Error('No se encontró guión en slotKey');
      }
      
      const dateStr = slotKey.substring(0, lastDashIndex);
      const hour = slotKey.substring(lastDashIndex + 1);
      
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      const isValidDateFormat = dateRegex.test(dateStr);
      
      if (!isValidDateFormat) {
        throw new Error(`Formato de fecha invÃ¡lido: "${dateStr}"`);
      }
      
      const hourRegex = /^\d{1,2}$/;
      const isValidHourFormat = hourRegex.test(hour);
      
      if (!isValidHourFormat) {
        throw new Error(`Formato de hora invÃ¡lido: "${hour}"`);
      }
      
      const [year, month, day] = dateStr.split('-').map(Number);
      
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        throw new Error(`Valores no numÃ©ricos: year=${year}, month=${month}, day=${day}`);
      }
      
      if (year < 2024 || year > 2030) {
        throw new Error(`AÃ±o fuera de rango: ${year}`);
      }
      if (month < 1 || month > 12) {
        throw new Error(`Mes fuera de rango: ${month}`);
      }
      if (day < 1 || day > 31) {
        throw new Error(`Día fuera de rango: ${day}`);
      }
      
      const date = new Date(year, month - 1, day); // month-1 porque Date usa 0-11
      
      if (isNaN(date.getTime())) {
        throw new Error('Date object invÃ¡lido despuÃ©s de creación');
      }
      
      const isConsistent = (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      );
      
      if (!isConsistent) {
        throw new Error('Fecha inconsistente despuÃ©s de creación');
      }
      
      const formattedDate = format(date, 'dd/MM/yyyy');
      const formattedDay = format(date, 'EEEE', { locale: es });
      
      const result = {
        fecha: formattedDate,
        dia: formattedDay,
        hora: `${hour}:00`
      };
      
      return result;
      
    } catch (error) {
      console.error('âŒ ERROR en formatSlotInfo:', error);
      console.error('âŒ SlotKey problemÃ¡tico:', slotKey);
      
      let fallbackHour = '00';
      try {
        const lastDash = slotKey.lastIndexOf('-');
        if (lastDash !== -1) {
          fallbackHour = slotKey.substring(lastDash + 1);
        }
      } catch (e) {
        console.error('âŒ Error extrayendo hora para fallback:', e);
      }
      
      return {
        fecha: 'Fecha invÃ¡lida',
        dia: 'Día invÃ¡lido', 
        hora: `${fallbackHour}:00`
      };
    }
  };

  // Navegación de semanas
  const goToPreviousWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const goToNextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));
  const goToCurrentWeek = () => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));

  // No mostrar si no hay especialista seleccionado
  if (!selectedEspecialista) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header con slots seleccionados */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Seleccionar Horarios: {selectedEspecialista?.usuario?.nombre} {selectedEspecialista?.usuario?.apellidos}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Semana del {format(currentWeek, 'd')} al {format(addDays(currentWeek, 6), 'd MMMM yyyy', { locale: es })}
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Controles de navegación */}
              <IconButton onClick={goToPreviousWeek} disabled={loading}>
                <ChevronLeft />
              </IconButton>
              <Button onClick={goToCurrentWeek} size="small" disabled={loading}>
                Hoy
              </Button>
              <IconButton onClick={goToNextWeek} disabled={loading}>
                <ChevronRight />
              </IconButton>
            </Stack>
          </Stack>

          {/* Mostrar slots seleccionados */}
          {selectedSlots.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="subtitle2" color="primary">
                  Horarios Seleccionados ({selectedSlots.length}):
                </Typography>
                <Button
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearAll}
                  color="secondary"
                >
                  Limpiar Todo
                </Button>
              </Stack>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedSlots.map((slotKey) => {
                  const slotInfo = formatSlotInfo(slotKey);
                  return (
                    <Chip
                      key={slotKey}
                      label={`${slotInfo.dia} ${slotInfo.fecha} - ${slotInfo.hora}`}
                      onDelete={() => {
                        const newSlots = selectedSlots.filter(slot => slot !== slotKey);
                        onSlotsChange?.(newSlots);
                      }}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Grid de horarios */}
      <Paper sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: isMobile ? 700 : 800 }}>
              {/* Header de días */}
              <Grid container sx={{ backgroundColor: theme.palette.grey[100] }}>
                <Grid item xs={1}>
                  <Box sx={{ p: 1, textAlign: 'center', fontWeight: 'bold' }}>
                    Hora
                  </Box>
                </Grid>
                {weekDays.map((date, index) => (
                  <Grid item xs key={index} sx={{ flex: 1 }}>
                    <Box 
                      sx={{ 
                        p: 1, 
                        textAlign: 'center', 
                        fontWeight: 'bold',
                        backgroundColor: isSameDay(date, new Date()) 
                          ? alpha(theme.palette.primary.main, 0.1) 
                          : 'transparent'
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {DAYS[index]}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(date, 'd MMM', { locale: es })}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Filas de horarios */}
              {HOURS.map(hour => (
                <Grid container key={hour} sx={{ minHeight: 30 }}>
                  {/* Columna de hora */}
                  <Grid item xs={1}>
                    <Box 
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.grey[50],
                        borderRight: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <Typography variant="caption" fontWeight="bold">
                        {hour}:00
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Columnas de días */}
                  {weekDays.map((date, dayIndex) => {
                    // âœ… Obtener cita activa (sin reprogramadas)
                    const citaActiva = getCitaForSlot(date, hour);
                    // âœ… Obtener todas las citas (incluyendo históricas)
                    const todasLasCitas = getAllCitasForSlot(date, hour);
                    
                    return (
                      <Grid item xs key={dayIndex} sx={{ flex: 1 }}>
                        <Box 
                          sx={{ 
                            height: 40,
                            p: 0.5,
                            borderRight: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            backgroundColor: isSameDay(date, new Date()) 
                              ? alpha(theme.palette.primary.main, 0.05) 
                              : 'transparent'
                          }}
                        >
                          {citaActiva ? 
                            renderCitaCell(citaActiva, todasLasCitas) : 
                            renderAvailableSlot(date, hour)}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Leyenda actualizada */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>
            Leyenda
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Chip 
              icon={<CheckIcon />}
              label="Confirmada" 
              sx={{ backgroundColor: '#4CAF50', color: 'white' }}
              size="small"
            />
            <Chip 
              icon={<ScheduleIcon />}
              label="Programada" 
              sx={{ backgroundColor: '#FF9800', color: 'white' }}
              size="small"
            />
            <Chip 
              icon={<PersonIcon />}
              label="Evento Personal" 
              sx={{ backgroundColor: '#6D6D6D', color: 'white' }}
              size="small"
            />
            <Chip 
              icon={<PersonIcon />}
              label="Cita Grupal" 
              sx={{ backgroundColor: '#2196F3', color: 'white', border: '2px solid #1976D2' }}
              size="small"
            />
            {/* âœ… NUEVA LEYENDA */}
            <Chip 
              label="Con Historial" 
              sx={{ backgroundColor: '#ff6f00', color: 'white', border: '2px dashed #ff6f00' }}
              size="small"
            />
            <Chip 
              icon={<CheckIcon />}
              label="Slot Seleccionado" 
              color="primary"
              variant="outlined"
              size="small"
            />
            <Chip 
              icon={<AddIcon />}
              label="Disponible - Click para seleccionar" 
              variant="outlined"
              size="small"
            />
          </Stack>
        </CardContent>
      </Card>

      {/* âœ… DIALOG MEJORADO - Muestra información completa incluyendo historial */}
      <Dialog
        open={citaDialogOpen}
        onClose={() => setCitaDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedCita && (
          <>
            <DialogTitle>
              {selectedCita.es_evento_personal ? 'Evento Personal' : 
               esCitaGrupal(selectedCita) ? 'Cita Grupal' : 'Detalle de Cita'}
               
              {/* âœ… Indicador si tiene historial */}
              {selectedCita.citasHistoricas?.length > 0 && (
                <Chip
                  label={`${selectedCita.citasHistoricas.length} Reprogramadas`}
                  size="small"
                  sx={{ ml: 1, bgcolor: '#ff6f00', color: 'white' }}
                />
              )}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2}>
                {/* âœ… NUEVA SECCIÃ“N - Mostrar citas históricas si existen */}
                {selectedCita.citasHistoricas?.length > 0 && (
                  <Alert severity="info" variant="outlined">
                    <Typography variant="subtitle2" gutterBottom>
                      Historial de este horario:
                    </Typography>
                    {selectedCita.citasHistoricas.map((citaHist, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        â€¢ <strong>{citaHist.estado}:</strong> {citaHist.paciente?.nombres} {citaHist.paciente?.apellidos}
                        {citaHist.notas && (
                          <Typography variant="caption" display="block" sx={{ ml: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                            "{citaHist.notas}"
                          </Typography>
                        )}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {/* Información de cita grupal */}
                {esCitaGrupal(selectedCita) && (
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'info.light', 
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'info.main'
                  }}>
                    <Typography variant="h6" color="info.main" gutterBottom>
                      ðŸ§‘â€ðŸ¤â€ðŸ§‘ Cita Grupal
                    </Typography>
                    {selectedCita.grupo?.nombre && (
                      <Typography variant="body2">
                        <strong>Grupo:</strong> {selectedCita.grupo.nombre}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Pacientes en esta cita:</strong>
                    </Typography>
                    {getCitasGrupalesEnSlot(
                      new Date(selectedCita.fecha), 
                      parseInt(selectedCita.hora_inicio.split(':')[0])
                    ).map((citaGrupal, index) => (
                      <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                        â€¢ {citaGrupal.paciente?.nombres} {citaGrupal.paciente?.apellidos}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" color="primary">
                    Fecha y Hora
                  </Typography>
                  <Typography>
                    {format(parseISO(selectedCita.fecha), 'EEEE d MMMM yyyy', { locale: es })} | 
                    {selectedCita.hora_inicio} - {selectedCita.hora_fin}
                  </Typography>
                </Box>
                
                {!selectedCita.es_evento_personal && selectedCita.paciente && !esCitaGrupal(selectedCita) && (
                  <Box>
                    <Typography variant="subtitle2" color="primary">
                      Paciente
                    </Typography>
                    <Typography>
                      {selectedCita.paciente.nombres} {selectedCita.paciente.apellidos}
                    </Typography>
                  </Box>
                )}
                
                {selectedCita.especialidad && (
                  <Box>
                    <Typography variant="subtitle2" color="primary">
                      Especialidad
                    </Typography>
                    <Typography>
                      {selectedCita.especialidad.nombre}
                    </Typography>
                  </Box>
                )}
                
                <Box>
                  <Typography variant="subtitle2" color="primary">
                    Estado
                  </Typography>
                  <Chip
                    label={selectedCita.estado}
                    sx={{ 
                      bgcolor: getEstadoColor(selectedCita.estado),
                      color: 'white'
                    }}
                    size="small"
                  />
                </Box>
                
                {selectedCita.motivo && (
                  <Box>
                    <Typography variant="subtitle2" color="primary">
                      {selectedCita.es_evento_personal ? 'Descripción' : 'Motivo'}
                    </Typography>
                    <Typography>
                      {selectedCita.motivo}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCitaDialogOpen(false)}>
                Cerrar
              </Button>
              <Button
                startIcon={<EditIcon />}
                onClick={() => {
                  onCitaClick?.(selectedCita);
                  setCitaDialogOpen(false);
                }}
              >
                Editar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AvailabilityScheduler;